import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Upload, Search, History, BarChart3, ArrowRight } from 'lucide-react';

const DocumentLibrary = () => {
  const documentFeatures = [
    {
      id: 'upload',
      title: 'Upload Document',
      description: 'Seamlessly upload and organize documents with metadata.',
      icon: <Upload size={24} />,
      path: '/collaboration/documents/upload',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      id: 'search',
      title: 'Search & Tag Documents',
      description: 'Find documents quickly with intelligent search and tagging.',
      icon: <Search size={24} />,
      path: '/collaboration/documents/search',
      color: 'bg-green-50 text-green-600'
    },
    {
      id: 'history',
      title: 'Version History',
      description: 'Track document changes and maintain complete history.',
      icon: <History size={24} />,
      path: '/collaboration/documents/history',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      id: 'reports',
      title: 'Document Reports',
      description: 'Generate comprehensive reports on document usage.',
      icon: <BarChart3 size={24} />,
      path: '/collaboration/documents/reports',
      color: 'bg-orange-50 text-orange-600'
    }
  ];

  return (
    <div className="document-library" style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white/20 p-4 rounded-2xl">
                <FileText size={48} />
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-4">Document Library</h1>
            <p className="text-xl text-indigo-100 max-w-3xl mx-auto">
              Centralized document management with advanced search, version control, and collaboration features.
            </p>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Document Management</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive tools for organizing, searching, and managing your documents
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {documentFeatures.map((feature) => (
              <Link
                key={feature.id}
                to={feature.path}
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-indigo-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg ${feature.color}`}>
                    {feature.icon}
                  </div>
                  <ArrowRight 
                    size={20} 
                    className="text-gray-400 group-hover:text-indigo-600 transition-colors" 
                  />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors">
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Start Managing Your Documents
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Upload, organize, and collaborate on documents with our powerful document management system.
          </p>
          <Link
            to="/collaboration/documents/upload"
            className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Upload size={20} className="mr-2" />
            Upload Document
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DocumentLibrary;
